import { SSelect } from "@avalara/skylab-sdk/react";
import React, { useMemo } from "react";
import { testIDs } from "../../../end-to-end-tests/constants/testIDs";
import { applyOptionToggle, EXPORT_OPTION_DEFINITIONS, type ExportOptions } from "./exportOptions";

export interface ExportCompany {
    value: string;
    label: string;
}

interface SkylabSelectEvent extends CustomEvent {
    detail: { item: { value: string }; isDeselectAll?: boolean };
}

interface ExportOptionsFormProps {
    companies: ExportCompany[];
    selectedCompanyIds: string[];
    options: ExportOptions;
    disabled?: boolean;
    onCompaniesChange: (ids: string[]) => void;
    onOptionsChange: (options: ExportOptions) => void;
}

const INDENT_REM = 1.5;

const ExportOptionsForm: React.FC<ExportOptionsFormProps> = ({
    companies,
    selectedCompanyIds,
    options,
    disabled = false,
    onCompaniesChange,
    onOptionsChange,
}) => {
    const selected = useMemo(() => new Set(selectedCompanyIds), [selectedCompanyIds]);

    const companyOptions = useMemo(
        () =>
            companies.map(company => ({
                label: company.label,
                value: company.value,
                selected: selected.has(company.value),
            })),
        [companies, selected]
    );

    const addCompany = (e: Event): void => {
        const value = (e as SkylabSelectEvent).detail?.item?.value;
        if (value && !selected.has(value)) {
            onCompaniesChange([...selectedCompanyIds, value]);
        }
    };

    const removeCompany = (e: Event): void => {
        const detail = (e as SkylabSelectEvent).detail;
        if (detail?.isDeselectAll) {
            onCompaniesChange([]);
            return;
        }
        const value = detail?.item?.value;
        if (value) {
            onCompaniesChange(selectedCompanyIds.filter(id => id !== value));
        }
    };

    return (
        <div>
            <fieldset className="data-transfer-fieldset" disabled={disabled}>
                <legend className="data-transfer-legend">1. Select companies to export</legend>
                {companies.length === 0 ? (
                    <p className="margin-none">No companies available.</p>
                ) : (
                    <div className="data-transfer-company-select">
                        <label htmlFor="data-transfer-companies" id="data-transfer-companies-label">
                            Companies
                        </label>
                        <SSelect
                            inputId="data-transfer-companies"
                            data-testid={testIDs.dataTransfer.companySelect}
                            optionsList={companyOptions}
                            multiple
                            selectionOptional
                            showSelectionCount={selected.size > 2}
                            disabled={disabled}
                            onS-select={addCompany}
                            onS-deselect={removeCompany}
                        />
                    </div>
                )}
            </fieldset>

            <fieldset className="data-transfer-fieldset" disabled={disabled}>
                <legend className="data-transfer-legend">2. Choose what to include</legend>
                {EXPORT_OPTION_DEFINITIONS.map((option, index) => (
                    <div key={option.key} style={{ paddingLeft: `${index * INDENT_REM}rem` }}>
                        <input
                            type="checkbox"
                            id={`data-transfer-option-${option.key}`}
                            data-testid={`${testIDs.dataTransfer.option}-${option.key}`}
                            checked={options[option.key]}
                            onChange={e =>
                                onOptionsChange(
                                    applyOptionToggle(options, option.key, e.target.checked)
                                )
                            }
                        />
                        <label htmlFor={`data-transfer-option-${option.key}`}>{option.label}</label>
                    </div>
                ))}
                <p className="data-transfer-count margin-top-xs margin-bottom-none">
                    Account settings are always included.
                </p>
            </fieldset>
        </div>
    );
};

export default ExportOptionsForm;
